import React, { FunctionComponent, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { AccessibilityRolesNative } from '@lyrahealth-inc/ui-core-crossplatform/src/constants'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpTestPlanSection } from '../npTestPlanConstants'

export interface NpTestPlanProgressStep {
  section: NpTestPlanSection
  displayName: string
  meta?: string
  details?: ReactNode
}

export interface NpTestPlanProgressProps {
  steps: NpTestPlanProgressStep[]
  activeSection: NpTestPlanSection
}

const Container = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  width: '320px',
  flexShrink: 0,
  borderWidth: 1,
  borderColor: colors.borderDefault,
  borderStyle: 'solid',
  borderRadius: '8px',
  overflow: 'hidden',
}))

const Step = styled.View<{ theme: ThemeType; isActive: boolean; isFirst: boolean }>(
  ({ theme: { colors, spacing }, isActive, isFirst }) => ({
    gap: spacing['16px'],
    padding: spacing['24px'],
    backgroundColor: isActive ? colors.backgroundSection : colors.backgroundPrimary,
    ...(!isFirst && {
      borderTopWidth: 1,
      borderTopColor: colors.borderDefault,
      borderStyle: 'solid',
    }),
  }),
)

const StepHeading = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing['8px'],
}))

/**
 * Deliberately not navigation: the provider is moved between steps by completing work, and reaches
 * the later ones from screens that do not exist yet, so nothing here is pressable.
 */
export const NpTestPlanProgress: FunctionComponent<NpTestPlanProgressProps> = ({ steps, activeSection }) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType

  const notReachedLabel = formatMessage({
    defaultMessage: 'not available yet',
    description: 'Announced after the name of a neuropsych test plan step the provider cannot reach yet',
  })

  return (
    <Container testID={tID('NpTestPlanProgress')}>
      {steps.map(({ section, displayName, meta, details }, index) => {
        const isActive = section === activeSection
        return (
          <Step
            key={section}
            testID={tID(`NpTestPlanProgress-${section}`)}
            accessibilityRole={AccessibilityRolesNative.TEXT}
            accessibilityLabel={isActive ? displayName : `${displayName}, ${notReachedLabel}`}
            isActive={isActive}
            isFirst={index === 0}
          >
            <StepHeading>
              <BodyText
                text={displayName}
                size={BodyTextSize.LARGE}
                color={isActive ? colors.textActive : colors.textInactive}
              />
              {meta && (
                <BodyText
                  testID={tID(`NpTestPlanProgress-${section}-meta`)}
                  text={meta}
                  size={BodyTextSize.CAPTION}
                  color={colors.textSecondary}
                />
              )}
            </StepHeading>
            {details}
          </Step>
        )
      })}
    </Container>
  )
}
